import { matrixTitle } from '@/lib/matrix-grid-filters';
import { matrixDisplayState } from '@/lib/matrix-grid-search';
import type { MatrixFolder, MatrixSummary } from '@mss_creator/shared';
import { ActionMenu, type ActionMenuItem, Button } from '@pms-digital/design-system';

export interface MatrixActionsMenuProps {
  matrix: MatrixSummary;
  /** The company's folders, for « Déplacer vers ». */
  folders: readonly MatrixFolder[];
  /** Settles when the copy exists or failed. */
  onDuplicate: (matrix: MatrixSummary) => Promise<unknown>;
  /** Settles when the wizard opened or the call failed. */
  onDerive: (matrix: MatrixSummary) => Promise<unknown>;
  /** Settles when the matrix has moved (to `folderId`, or « Hors dossier » for `null`). */
  onMove: (matrix: MatrixSummary, folderId: string | null) => Promise<unknown>;
  /** « Nouveau dossier… »: opens the creation dialog, which then moves the matrix in. */
  onNewFolder: (matrix: MatrixSummary) => void;
  onDelete: (matrix: MatrixSummary) => void;
}

/**
 * The « ⋯ » menu of a grid card and of a list row (issues #16 and #19,
 * « Menu ⋯ »). Terminée and Brouillon offer « Dupliquer » and « Nouvelle
 * matrice depuis celle-ci »; Échec and Génération en cours do not (absent, not
 * greyed out: the API refuses them with a 409). « Déplacer vers » and
 * « Supprimer » are offered in every state.
 *
 * « Déplacer vers » is one entry per destination in the same menu (the package's
 * `ActionMenu` has no second level): « Hors dossier », each folder, and
 * « Nouveau dossier… ». The current folder is not offered.
 */
export function MatrixActionsMenu({
  matrix,
  folders,
  onDuplicate,
  onDerive,
  onMove,
  onNewFolder,
  onDelete,
}: MatrixActionsMenuProps) {
  const state = matrixDisplayState(matrix);
  const canCopy = state === 'terminee' || state === 'brouillon';

  const items: ActionMenuItem[] = [
    ...(canCopy
      ? [
          {
            label: 'Dupliquer',
            icon: 'content_copy',
            onSelect: () => void onDuplicate(matrix),
          },
          {
            label: 'Nouvelle matrice depuis celle-ci',
            icon: 'note_add',
            onSelect: () => void onDerive(matrix),
          },
        ]
      : []),
    ...(matrix.folderId === null
      ? []
      : [
          {
            label: 'Déplacer vers : Hors dossier',
            icon: 'drive_file_move',
            onSelect: () => void onMove(matrix, null),
          },
        ]),
    ...folders
      .filter((folder) => folder.id !== matrix.folderId)
      .map((folder) => ({
        label: `Déplacer vers : ${folder.name}`,
        icon: 'drive_file_move',
        onSelect: () => void onMove(matrix, folder.id),
      })),
    {
      label: 'Déplacer vers : Nouveau dossier…',
      icon: 'create_new_folder',
      onSelect: () => onNewFolder(matrix),
    },
    { label: 'Supprimer', icon: 'delete', destructive: true, onSelect: () => onDelete(matrix) },
  ];

  return (
    <ActionMenu
      align="end"
      trigger={
        <Button
          type="button"
          variant="ghost"
          size="icon"
          icon="more_horiz"
          aria-label={`Actions pour « ${matrixTitle(matrix)} »`}
        />
      }
      items={items}
    />
  );
}
